/* eslint-disable style/quote-props */
import { Box, styled as muiStyled } from '@mui/material';
import { P, screenMdMin } from '@shared/styles';

// Base question wrapper with consistent card styling
export const BaseQuestionWrapper = muiStyled(Box, {
  shouldForwardProp: prop => !['$fullWidth', '$marginBottom', '$autoHeight', '$flexWrap'].includes(prop as string),
})<{
  $fullWidth?: boolean;
  $marginBottom?: string;
  $autoHeight?: boolean;
  $flexWrap?: string;
}>((props) => {
  const { $fullWidth, $marginBottom, $autoHeight, $flexWrap } = props;

  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 'var(--spacing-sm)',
    margin: 'var(--spacing-sm) 0',
    marginBottom: $marginBottom || '12px',
    padding: 'var(--spacing-md)',
    border: '1px solid var(--color-grey)',
    borderRadius: '12px',
    background: '#fff',
    boxShadow: '0 8px 24px rgba(16, 24, 40, 0.06)',
    flexDirection: 'column' as const,
    flexWrap: ($flexWrap || 'nowrap') as any,
    width: '100%',
    height: $autoHeight ? 'auto' : '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    '&:focus-within': {
      borderColor: '#78bbf1',
      boxShadow: '0 0 0 3px rgba(120, 187, 241, 0.25), 0 8px 24px rgba(16, 24, 40, 0.06)',
    },
    [`@media ${screenMdMin}`]: {
      flexDirection: ($fullWidth ? 'column' : 'row') as any,
      alignItems: $fullWidth ? 'flex-start' : 'center',
    },
  };
});

// Consistent question text wrapper for responsive layout
export const BaseQuestionTextWrapper = muiStyled(Box, {
  shouldForwardProp: prop => !['$isWide', '$fullWidth'].includes(prop as string),
})<{
  $isWide?: boolean;
  $fullWidth?: boolean;
}>(({ $isWide, $fullWidth }) => ({
  width: '100%',
  [`@media ${screenMdMin}`]: {
    width: $fullWidth ? '100%' : $isWide ? '32%' : '24%',
    minWidth: $fullWidth ? 'auto' : '240px',
  },
}));

// Consistent question text styling
export const BaseQuestionText = muiStyled(P)({
  fontSize: '15px',
  fontWeight: 600,
  color: 'var(--color-black)',
  margin: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
});

// Header row for questions with additional elements
export const QuestionHeaderRow = muiStyled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '8px',
  flexWrap: 'wrap',
  minWidth: 0,
  width: '100%',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

// Label group for structured question headers
export const QuestionLabelGroup = muiStyled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
});

// Required badge for mandatory questions
export const RequiredBadge = muiStyled('span')({
  display: 'inline-block',
  marginLeft: '8px',
  fontSize: '11px',
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: '999px',
  background: '#eef6ff',
  color: '#0b74da',
});

// Helper text for additional question context
export const QuestionHelperText = muiStyled(Box, {
  shouldForwardProp: prop => prop !== '$short',
})<{ $short?: boolean }>(({ $short }) => ({
  fontSize: '12px',
  color: '#888',
  marginTop: '4px',
  marginBottom: '12px',
  maxWidth: $short ? '50%' : '100%',
}));

// Subtle text for secondary information
export const SubtleText = muiStyled('span')({
  fontSize: '12px',
  color: '#6b7280',
});

// Score badge for rating displays
export const ScoreBadge = muiStyled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '40px',
  height: '28px',
  padding: '0 8px',
  fontSize: '13px',
  fontWeight: 700,
  color: '#0f766e',
  background: '#ecfdf5',
  border: '1px solid #99f6e4',
  borderRadius: '999px',
  marginTop: '4px',
  position: 'absolute',
  top: '6px',
  right: '6px',
});

// Answer wrapper for form inputs
export const AnswerWrapper = muiStyled(Box, {
  shouldForwardProp: prop => prop !== '$width',
})<{ $width?: string }>(({ $width }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
  width: $width || '100%',
}));

// Counter for text limits, etc.
export const Counter = muiStyled(Box, {
  shouldForwardProp: prop => prop !== '$warn',
})<{ $warn?: boolean }>(({ $warn }) => ({
  fontSize: '12px',
  color: $warn ? '#d14343' : '#6b7280',
}));
